import React, { useState } from 'react';
import {
  Stack,
  Typography,
  CircularProgress,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ChipBadge } from 'new/elements/ChipBadge';
import { TextField } from 'new/elements/TextField';
import { useDebouncedValue } from 'new/hooks/useDebouncedValue';
import {
  useListGatewayToolsQuery,
  useSemanticSearchGatewayToolsMutation,
} from '../../../state/aiApi';
import type { ApiGatewayTool, ApiGatewayToolSearchResult } from '../../../state/aiApiTypes';
import { getToolDisplayName } from '../../../utils/toolLabels';
import styles from './GatewaySection.module.scss';

type SearchMode = 'keyword' | 'semantic';
type ToolEntry = ApiGatewayTool | ApiGatewayToolSearchResult;

function isSearchResult(tool: ToolEntry): tool is ApiGatewayToolSearchResult {
  return 'similarity' in tool;
}

function getSourceTypeLabel(sourceType: string): string {
  switch (sourceType) {
    case 'nexla_vendor':
      return 'Nexla Vendor';
    case 'external_mcp':
      return 'External MCP';
    default:
      return sourceType;
  }
}

function getSourceTypeColor(sourceType: string): 'primary' | 'secondary' {
  return sourceType === 'nexla_vendor' ? 'primary' : 'secondary';
}

const ToolRow: React.FC<{ tool: ToolEntry }> = ({ tool }) => (
  <div className={styles.toolRow}>
    <div className={styles.toolRowMain}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ columnGap: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {getToolDisplayName(tool)}
        </Typography>
        <ChipBadge
          label={getSourceTypeLabel(tool.source_type)}
          color={getSourceTypeColor(tool.source_type)}
          variant="outlined"
        />
        {tool.category && <ChipBadge label={tool.category} variant="outlined" />}
        {tool.endpoint_type && <ChipBadge label={tool.endpoint_type} variant="outlined" />}
        {isSearchResult(tool) && (
          <Typography variant="caption" color="text.secondary">
            {Math.round(tool.similarity * 100)}% match
          </Typography>
        )}
      </Stack>
      {tool.description && (
        <Typography variant="caption" color="text.secondary" className={styles.toolDescription}>
          {tool.description}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" className={styles.toolName}>
        {tool.tool_name}
      </Typography>
    </div>
  </div>
);

interface GatewayToolsCatalogProps {
  toolsetId: number;
}

const GatewayToolsCatalog: React.FC<GatewayToolsCatalogProps> = ({ toolsetId }) => {
  const [searchMode, setSearchMode] = useState<SearchMode>('keyword');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText, 400);

  const [semanticSearch, { isLoading: isSearching, data: semanticResults }] =
    useSemanticSearchGatewayToolsMutation();

  const { data: keywordTools = [], isFetching: isKeywordFetching } = useListGatewayToolsQuery(
    { toolsetId, search: debouncedSearch || undefined },
    { skip: searchMode === 'semantic' },
  );

  const handleSearchModeChange = (_: React.MouseEvent, mode: SearchMode | null) => {
    if (mode) {
      setSearchMode(mode);
      setSearchText('');
    }
  };

  const handleSemanticSearch = async (term: string) => {
    if (!term.trim()) return;
    await semanticSearch({ toolsetId, data: { search_term: term, limit: 20 } });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchMode === 'semantic') {
      handleSemanticSearch(searchText);
    }
  };

  const isLoading = searchMode === 'keyword' ? isKeywordFetching : isSearching;
  const tools: ToolEntry[] =
    searchMode === 'semantic'
      ? Array.isArray(semanticResults)
        ? semanticResults
        : []
      : keywordTools;

  return (
    <div className={styles.panel}>
      <Typography variant="subtitle2" className={styles.panelTitle} mb={1.5}>
        Tools Catalog
      </Typography>

      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
        <TextField
          placeholder={
            searchMode === 'keyword' ? 'Filter tools...' : 'Search by meaning (press Enter)'
          }
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          startAdornment={<SearchIcon fontSize="small" />}
          className={styles.searchField}
        />
        <ToggleButtonGroup
          className={styles.toggleButtonsGroup}
          value={searchMode}
          exclusive
          onChange={handleSearchModeChange}
          size="small"
        >
          <ToggleButton className={styles.toggleButton} value="keyword">
            Keyword
          </ToggleButton>
          <ToggleButton className={styles.toggleButton} value="semantic">
            Semantic
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={20} />
        </Box>
      ) : tools.length === 0 ? (
        <Typography variant="body2" color="text.secondary" className={styles.emptyText}>
          {searchText
            ? 'No tools match your search.'
            : 'No tools in the gateway catalog yet. Sync an external MCP server to populate tools.'}
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {tools.map((tool) => (
            <ToolRow key={`${tool.source_type}-${tool.id}`} tool={tool} />
          ))}
        </Stack>
      )}
    </div>
  );
};

export default GatewayToolsCatalog;
